import React from 'react';

interface Testimonial {
  name: string;
  title: string;
  platform: 'upwork' | 'linkedin';
  date: string;
  rating: number;
  quote: string;
}

const StarRating: React.FC<{ rating: number }> = ({ rating }) => (
  <div className="flex gap-0.5 mb-3">
    {Array.from({ length: rating }).map((_, i) => (
      <svg key={i} className="w-5 h-5 text-amber-400 fill-amber-400" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ))}
  </div>
);

const PlatformBadge: React.FC<{ platform: 'upwork' | 'linkedin' }> = ({ platform }) => {
  if (platform === 'upwork') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#14a800] bg-[#14a800]/10 px-2.5 py-1 rounded-full">
        <svg className="w-3.5 h-3.5 fill-[#14a800]" viewBox="0 0 24 24">
          <path d="M18.561 13.158c-1.102 0-2.135-.467-3.074-1.227l.228-1.076.008-.042c.207-1.143.849-3.06 2.839-3.06 1.492 0 2.703 1.212 2.703 2.703-.001 1.489-1.212 2.702-2.704 2.702zm0-8.14c-2.539 0-4.51 1.649-5.31 4.366-1.22-1.834-2.148-4.036-2.687-5.892H8.476v7.282c-.001 1.402-1.141 2.542-2.543 2.542-1.401 0-2.541-1.14-2.541-2.542V3.492H1.313v7.282c0 2.715 2.208 4.923 4.923 4.923s4.923-2.208 4.923-4.923v-1.221c.527 1.099 1.187 2.213 1.977 3.234l-1.677 7.872h2.594l1.213-5.702c1.063.679 2.285 1.109 3.615 1.109 3.013 0 5.466-2.454 5.466-5.466 0-3.012-2.453-5.466-5.466-5.466z"/>
        </svg>
        Upwork
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0077b5] bg-[#0077b5]/10 px-2.5 py-1 rounded-full">
      <svg className="w-3.5 h-3.5 fill-[#0077b5]" viewBox="0 0 24 24">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
      LinkedIn
    </span>
  );
};

const Testimonials: React.FC = () => {
  const testimonials: Testimonial[] = [
    {
      name: 'Jamie',
      title: 'Client',
      platform: 'upwork',
      date: 'Dec 2025',
      rating: 5,
      quote: 'Vinay has been fantastic for us. Detailed, driven, clear at communicating, and we\'re really enjoying working with him to further improve the LLM and chat functionality in our app. Vinay has assisted and helped build several key features, as well as building advanced guardrails to ensure outputs match for compliance.'
    },
    {
      name: 'AI Avenue',
      title: 'Client',
      platform: 'upwork',
      date: 'Mar 2026',
      rating: 5,
      quote: 'Vinay has been a valuable AI expert/consultant to our team over the past several months, and our collaboration is very much ongoing. Throughout this time, he has consistently demonstrated a strong commitment to quality, delivering work that meets our standards reliably and on schedule. We are pleased with his professionalism and look forward to continuing to work with him.'
    },
    {
      name: 'Private Client',
      title: 'Verified Upwork review',
      platform: 'upwork',
      date: 'May 2026',
      rating: 5,
      quote: 'Vinay has done an outstanding work on several challenging AI tasks for our company. We will definitely reengage when new projects come up in the future. Thanks for such a good work!'
    },
    {
      name: 'Adam Murphy',
      title: 'Freelance LLM Engineer | AWS ML Specialty Certified',
      platform: 'linkedin',
      date: 'Nov 2025',
      rating: 5,
      quote: 'Vinay is a rare full-stack + LLM hybrid who consistently delivers. At Gains App, he combined senior-level product engineering with fast-rising LLM expertise to ship real, business-ready systems on deadline. He implemented an agentic, multi-agent architecture for our financial advice chatbot that handled diverse user queries with accuracy and robustness. Leveraging the LangChain ecosystem, he built clean pipelines, instrumented them well, and put strong input/output guardrails in place.'
    }
  ];

  return (
    <section id="testimonials" className="section-padding bg-white">
      <div className="container-max">
        <div className="max-w-2xl mb-10">
          <h2 className="text-4xl md:text-5xl font-semibold text-black mb-4 leading-[1.05] tracking-[-0.02em]">
            What clients say
          </h2>
          <p className="text-lg text-neutral-600">
            Real feedback from clients on Upwork and LinkedIn.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {testimonials.map((t, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl p-6 flex flex-col border border-neutral-200"
            >
              <div className="flex items-center justify-between mb-3">
                <PlatformBadge platform={t.platform} />
                <span className="text-xs text-neutral-400">{t.date}</span>
              </div>

              <StarRating rating={t.rating} />

              <p className="text-neutral-700 leading-relaxed flex-1 text-[0.95rem] not-italic">
                "{t.quote}"
              </p>

              <div className="mt-4 pt-4 border-t border-neutral-100">
                <p className="font-semibold text-black">{t.name}</p>
                <p className="text-sm text-neutral-500">{t.title}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
